import { AnalyticsService } from '@core/services';
import { WorkContent } from '@core/services/site-content/site-content.types';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement, property, svg } from '@rxdi/lit-html';
import { rich } from '@shared/rich-text';
import { ArrowIcon, StudioStyles } from '@shared/styles/studio.styles';

/** Home: selected work / case studies. */
@Component({
  selector: 'rs-work',
  styles: [
    StudioStyles,
    css`
      .cases {
        display: grid;
        gap: clamp(20px, 3vw, 32px);
      }

      .case {
        display: grid;
        grid-template-columns: 1fr;
        overflow: hidden;
        transition: border-color 0.4s var(--rs-ease), transform 0.4s var(--rs-ease);
      }

      .case:hover {
        border-color: var(--rs-line-strong);
      }

      @media (min-width: 960px) {
        .case {
          grid-template-columns: 1.15fr 1fr;
          min-height: 520px;
        }
        .case.flip .visual {
          order: 2;
        }
      }

      .visual {
        position: relative;
        overflow: hidden;
        min-height: 280px;
        background: var(--rs-ink-2);
        border-bottom: 1px solid var(--rs-line);
      }

      @media (min-width: 960px) {
        .visual {
          border-bottom: none;
          border-right: 1px solid var(--rs-line);
        }
        .case.flip .visual {
          border-right: none;
          border-left: 1px solid var(--rs-line);
        }
      }

      .shot {
        position: absolute;
        inset: 40px -60px -40px 40px;
        border-radius: 14px;
        overflow: hidden;
        border: 1px solid var(--rs-line-strong);
        box-shadow: var(--rs-shadow-lg);
        transform: rotate(-2deg);
        transition: transform 0.6s var(--rs-ease);
      }

      .case.flip .shot {
        transform: rotate(2deg);
      }

      .case:hover .shot {
        transform: rotate(0deg) translateY(-6px);
      }

      .shot img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: left top;
      }

      .chrome {
        display: flex;
        gap: 6px;
        padding: 10px 12px;
        background: var(--rs-surface-2);
        border-bottom: 1px solid var(--rs-line);
      }

      .chrome i {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--rs-line-strong);
      }

      .glow {
        position: absolute;
        inset: auto -20% -40% -20%;
        height: 70%;
        background: radial-gradient(50% 50% at 50% 50%, var(--rs-plasma-soft), transparent 70%);
        pointer-events: none;
      }

      .case[data-id='steelfab'] .glow {
        background: radial-gradient(50% 50% at 50% 50%, var(--rs-ion-soft), transparent 70%);
      }

      .board {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        padding: 32px;
      }

      .board svg {
        width: 100%;
        max-width: 520px;
        height: auto;
      }

      .board .lane {
        fill: none;
        stroke: var(--rs-line-strong);
        stroke-dasharray: 4 6;
      }

      .board .card-a {
        fill: var(--rs-plasma-soft);
        stroke: var(--rs-plasma);
      }

      .board .card-b {
        fill: var(--rs-ion-soft);
        stroke: var(--rs-ion);
      }

      .board .hot {
        fill: var(--rs-signal-soft);
        stroke: var(--rs-signal);
        filter: drop-shadow(0 0 12px var(--rs-signal-glow));
        animation: lift 2.4s var(--rs-ease) infinite alternate;
      }

      .board .arc {
        fill: none;
        stroke: var(--rs-signal);
        stroke-width: 2;
        stroke-dasharray: 8 8;
        animation: strike 1.6s linear infinite;
      }

      .board .ping {
        fill: var(--rs-signal);
        animation: blink 1.6s ease-in-out infinite;
      }

      .board text {
        font-family: var(--rs-font-mono);
        font-size: 11px;
        fill: var(--rs-text-muted);
      }

      @keyframes lift {
        to {
          transform: translateY(-8px);
        }
      }

      @keyframes strike {
        to {
          stroke-dashoffset: -32;
        }
      }

      @keyframes blink {
        50% {
          opacity: 0.2;
        }
      }

      .body {
        display: flex;
        flex-direction: column;
        gap: 18px;
        padding: clamp(28px, 4vw, 52px);
      }

      .meta {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--rs-text-muted);
      }

      .num {
        color: var(--rs-signal);
      }

      .kind {
        padding: 4px 10px;
        border-radius: 999px;
        background: var(--rs-signal-soft);
        color: var(--rs-signal);
      }

      h3 {
        margin: 0;
        font-family: var(--rs-font-display);
        font-size: clamp(28px, 3.2vw, 42px);
        line-height: 1.05;
        letter-spacing: -0.03em;
      }

      p {
        margin: 0;
        color: var(--rs-text-2);
        line-height: 1.65;
      }

      .tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .link {
        margin-top: auto;
        padding-top: 8px;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        font-weight: 600;
        color: var(--rs-text);
        width: fit-content;
        border-bottom: 1px solid var(--rs-line-strong);
        padding-bottom: 4px;
        transition: border-color 0.3s var(--rs-ease);
      }

      .link svg {
        width: 16px;
        height: 16px;
        transition: transform 0.3s var(--rs-ease);
      }

      .link:hover {
        border-color: var(--rs-signal);
      }

      .link:hover svg {
        transform: translateX(4px);
      }
    `,
  ],
  template(this: WorkComponent) {
    const c = this.content;
    if (!c) {
      return html``;
    }
    return html`
      <section class="section" id="work">
        <div class="wrap">
          <div class="section-head split">
            <div>
              <span class="kicker"><span class="index">01</span> Selected work</span>
              <h2 class="h2">${rich(c.heading)}</h2>
            </div>
            <p class="lead">${rich(c.lead)}</p>
          </div>

          <div class="cases">
            ${c.cases.map(
              (c, i) => html`
                <article class="card case ${i % 2 ? 'flip' : ''}" data-id=${c.id}>
                  <div class="visual">
                    <div class="glow"></div>
                    ${c.image
                      ? html`<div class="shot">
                          <div class="chrome"><i></i><i></i><i></i></div>
                          <img src=${c.image} alt="${c.client} screenshot" loading="lazy" />
                        </div>`
                      : this.board()}
                  </div>
                  <div class="body">
                    <div class="meta">
                      <span class="num">0${i + 1}</span>
                      <span class="kind">${c.kind}</span>
                      <span>${c.client}</span>
                    </div>
                    <h3>${c.title}</h3>
                    <p>${c.body}</p>
                    <div class="tags">${c.tags.map((t) => html`<span class="tag">${t}</span>`)}</div>
                    <a
                      class="link"
                      href=${c.url}
                      target="_blank"
                      rel="noopener"
                      @click=${() => this.analytics.track('work_open', { case: c.id })}
                    >
                      ${c.urlLabel} ${ArrowIcon}
                    </a>
                  </div>
                </article>
              `
            )}
          </div>
        </div>
      </section>
    `;
  },
})
export class WorkComponent extends LitElement {
  @property({ attribute: false }) content: WorkContent;

  @Inject(AnalyticsService)
  private analytics: AnalyticsService;

  /** Abstract game board for the card-game case (no third-party art). */
  private board() {
    const top = [60, 150, 240, 330, 420];
    return html`<div class="board">
      <svg viewBox="0 0 520 360" aria-label="Card game board illustration">
        <rect class="lane" x="20" y="20" width="480" height="140" rx="16"></rect>
        <rect class="lane" x="20" y="200" width="480" height="140" rx="16"></rect>
        ${top.map((x) => svg`<rect class="card-a" x=${x - 30} y="40" width="60" height="96" rx="10"></rect>`)}
        ${top.map((x, i) =>
          i === 3
            ? svg`<rect class="hot" x=${x - 30} y="222" width="60" height="96" rx="10"></rect>`
            : svg`<rect class="card-b" x=${x - 30} y="222" width="60" height="96" rx="10"></rect>`
        )}
        <path class="arc" d="M330 222 C 330 180, 150 190, 150 136"></path>
        <circle class="ping" cx="150" cy="136" r="5"></circle>
        <text x="28" y="186">opponent</text>
        <text x="420" y="186">you · turn 7</text>
      </svg>
    </div>`;
  }
}
